import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  LayoutDashboard,
  FileText,
  Users,
  CreditCard,
  FileCheck,
  BarChart3,
  Settings,
  Building2,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Receipt,
  MessageSquare,
  Palette,
  PieChart,
  UsersRound,
  RefreshCw,
  Key,
  Database,
  LifeBuoy,
  Ticket,
  Cog,
  Percent,
  Shield,
  Layers,
  Sparkles,
  Gift,
  ClipboardList,
  Truck
} from "lucide-react";
import axios from "axios";
import GuidedTour from "@/components/GuidedTour";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_factura-mexico-app/artifacts/u9456sea_Logo%20CIA.jpg";

const DashboardLayout = ({ isAdmin = false }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [maestroOpen, setMaestroOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    // Check if we should start the tour
    const shouldStartTour = localStorage.getItem('cia_start_tour');
    if (shouldStartTour === 'true' && !isAdmin) {
      localStorage.removeItem('cia_start_tour');
      // Delay tour start to let dashboard render
      setTimeout(() => setShowTour(true), 500);
    }
  }, [isAdmin]);

  useEffect(() => {
    // Load subscription info to determine plan
    const loadSubscription = async () => {
      try {
        const token = localStorage.getItem("cia_token");
        const response = await axios.get(`${API}/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(response.data);
      } catch (error) {
        // Default to basic if not found
        setSubscription({ plan: "basic" });
      }
    };
    if (!isAdmin) {
      loadSubscription();
    }
  }, [isAdmin]);

  const handleStartTour = () => {
    setShowTour(true);
  };

  const handleTourComplete = () => {
    setShowTour(false);
    localStorage.setItem('cia_tour_completed', 'true');
  };

  const handleTourSkip = () => {
    setShowTour(false);
  };

  // Check if user has enterprise plan
  const isEnterprise = subscription?.plan === "enterprise" || subscription?.plan === "empresarial";
  
  // Check if current user is admin of company (company role is the admin)
  const isCompanyAdmin = user?.role === "admin" || user?.role === "owner" || user?.role === "company";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Check if any maestro submenu is active
  const isMaestroActive = [
    "/dashboard/reports",
    "/dashboard/analytics", 
    "/dashboard/clients",
    "/dashboard/team"
  ].some(path => location.pathname.startsWith(path));

  // Check if any config submenu is active
  const isConfigActive = [
    "/dashboard/settings",
    "/dashboard/pdf-customizer",
    "/dashboard/tax-config"
  ].some(path => location.pathname.startsWith(path));

  // Auto-open submenus if active
  useEffect(() => {
    if (isMaestroActive) setMaestroOpen(true);
    if (isConfigActive) setConfigOpen(true);
  }, [isMaestroActive, isConfigActive]);

  const adminMenuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: Building2, label: "Empresas", path: "/admin/companies" },
    { icon: Ticket, label: "Soporte", path: "/admin/tickets" },
    { icon: Cog, label: "Configuración", path: "/admin/config" },
  ];

  if (isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''} transition-transform duration-300 lg:translate-x-0 flex flex-col`}>
          <div className="p-6 border-b border-slate-700 flex-shrink-0">
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="CIA Factura℗" className="h-8 w-auto brightness-200" />
              <span className="font-bold text-lg">CIA Factura℗</span>
            </Link>
          </div>

          <nav className="sidebar-nav">
            {adminMenuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-700 flex-shrink-0">
            <button
              onClick={handleLogout}
              className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </aside>

        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="main-content">
          <header className="glass-nav sticky top-0 z-20">
            <div className="flex items-center justify-between h-16 px-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
                >
                  {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <h1 className="text-lg font-semibold text-slate-900">Panel de Administración</h1>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#5B9BD5] flex items-center justify-center text-white font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden sm:inline text-slate-700">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-slate-900">{user?.name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="p-6">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  // Company Dashboard Layout
  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} transition-transform duration-300 lg:translate-x-0 flex flex-col`}>
        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CIA Factura℗" className="h-8 w-auto brightness-200" />
            <span className="font-bold text-lg">CIA Factura℗</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          {/* Dashboard */}
          <Link
            to="/dashboard"
            className={`sidebar-link ${location.pathname === "/dashboard" ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>

          {/* Asistente IA */}
          <Link
            to="/dashboard/chat"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/chat") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-asistente-ia"
          >
            <MessageSquare className="w-5 h-5" />
            <span>Asistente IA</span>
          </Link>

          {/* Facturas */}
          <Link
            to="/dashboard/invoices"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/invoices") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-facturas"
          >
            <FileText className="w-5 h-5" />
            <span>Facturas</span>
          </Link>

          {/* Cotizaciones */}
          <Link
            to="/dashboard/quotes"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/quotes") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-cotizaciones"
          >
            <ClipboardList className="w-5 h-5" />
            <span>Cotizaciones</span>
          </Link>

          {/* Carta Porte */}
          <Link
            to="/dashboard/carta-porte"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/carta-porte") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-carta-porte"
          >
            <Truck className="w-5 h-5" />
            <span>Carta Porte</span>
          </Link>

          {/* Notas de Crédito */}
          <Link
            to="/dashboard/credit-notes"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/credit-notes") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-notas-de-crédito"
          >
            <Receipt className="w-5 h-5" />
            <span>Notas de Crédito</span>
          </Link>

          {/* Complementos de Pago */}
          <Link
            to="/dashboard/payment-complements"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/payment-complements") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-complementos-de-pago"
          >
            <FileCheck className="w-5 h-5" />
            <span>Complementos de Pago</span>
          </Link>

          {/* Recurrentes */}
          <Link
            to="/dashboard/recurring"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/recurring") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-recurrentes"
          >
            <RefreshCw className="w-5 h-5" />
            <span>Recurrentes</span>
          </Link>

          {/* API Pública - Solo para plan Empresarial */}
          {isEnterprise && (
            <Link
              to="/dashboard/api-dashboard"
              className={`sidebar-link ${location.pathname.startsWith("/dashboard/api-dashboard") ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-api-dashboard"
            >
              <Key className="w-5 h-5" />
              <span>API Pública</span>
              <span className="ml-auto px-2 py-0.5 bg-indigo-500 text-white text-xs rounded-full">PRO</span>
            </Link>
          )}

          {/* Programa de Referidos */}
          <Link
            to="/dashboard/referrals"
            className={`sidebar-link ${location.pathname === "/dashboard/referrals" ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-referrals"
          >
            <Gift className="w-5 h-5" />
            <span>Referidos</span>
            <span className="ml-auto px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">🎁</span>
          </Link>

          {/* Soporte */}
          <Link
            to="/dashboard/support"
            className={`sidebar-link ${location.pathname.startsWith("/dashboard/support") ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-soporte"
          >
            <LifeBuoy className="w-5 h-5" />
            <span>Soporte</span>
          </Link>

          {/* MAESTRO - Collapsible Menu */}
          <Collapsible open={maestroOpen} onOpenChange={setMaestroOpen}>
            <CollapsibleTrigger className={`sidebar-link w-full justify-between ${isMaestroActive ? 'text-white bg-slate-700/50' : ''}`} data-testid="nav-maestro">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5" />
                <span>Maestro</span>
              </div>
              {maestroOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-4 space-y-1 mt-1">
              <Link
                to="/dashboard/clients"
                className={`sidebar-link text-sm ${location.pathname.startsWith("/dashboard/clients") ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-clientes"
              >
                <Users className="w-4 h-4" />
                <span>Clientes</span>
              </Link>
              <Link
                to="/dashboard/reports"
                className={`sidebar-link text-sm ${location.pathname.startsWith("/dashboard/reports") ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-reportes"
              >
                <BarChart3 className="w-4 h-4" />
                <span>Reportes</span>
              </Link>
              <Link
                to="/dashboard/analytics"
                className={`sidebar-link text-sm ${location.pathname.startsWith("/dashboard/analytics") ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-analytics"
              >
                <PieChart className="w-4 h-4" />
                <span>Analytics</span>
              </Link>
              <Link
                to="/dashboard/team"
                className={`sidebar-link text-sm ${location.pathname.startsWith("/dashboard/team") ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-equipo"
              >
                <UsersRound className="w-4 h-4" />
                <span>Equipo</span>
              </Link>
            </CollapsibleContent>
          </Collapsible>

          {/* CONFIGURACIÓN - Collapsible Menu */}
          <Collapsible open={configOpen} onOpenChange={setConfigOpen}>
            <CollapsibleTrigger className={`sidebar-link w-full justify-between ${isConfigActive ? 'text-white bg-slate-700/50' : ''}`} data-testid="nav-configuracion">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5" />
                <span>Configuración</span>
              </div>
              {configOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-4 space-y-1 mt-1">
              <Link
                to="/dashboard/settings"
                className={`sidebar-link text-sm ${location.pathname === "/dashboard/settings" ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-ajustes"
              >
                <Settings className="w-4 h-4" />
                <span>Ajustes</span>
              </Link>
              <Link
                to="/dashboard/tax-config"
                className={`sidebar-link text-sm ${location.pathname === "/dashboard/tax-config" ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-tax-config"
              >
                <Percent className="w-4 h-4" />
                <span>Configuración IVA</span>
              </Link>
              <Link
                to="/dashboard/certificates"
                className={`sidebar-link text-sm ${location.pathname === "/dashboard/certificates" ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-certificates"
              >
                <Shield className="w-4 h-4" />
                <span>CSD / FIEL</span>
              </Link>
              <Link
                to="/dashboard/multi-rfc"
                className={`sidebar-link text-sm ${location.pathname === "/dashboard/multi-rfc" ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-multi-rfc"
              >
                <Layers className="w-4 h-4" />
                <span>Multi-RFC</span>
              </Link>
              <Link
                to="/dashboard/pdf-customizer"
                className={`sidebar-link text-sm ${location.pathname.startsWith("/dashboard/pdf-customizer") ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-personalizar-pdf"
              >
                <Palette className="w-4 h-4" />
                <span>Personalizar PDF</span>
              </Link>
            </CollapsibleContent>
          </Collapsible>
        </nav>

        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="main-content">
        {/* Top navbar */}
        <header className="glass-nav sticky top-0 z-20">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
                data-testid="mobile-menu-btn"
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-lg font-semibold text-slate-900">Panel de Control</h1>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-[#5B9BD5] flex items-center justify-center text-white font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden sm:inline text-slate-700">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-slate-900">{user?.name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                    {subscription && (
                      <p className="text-xs text-[#5B9BD5] mt-1 capitalize">
                        Plan: {subscription.plan || 'Básico'}
                      </p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  {!isAdmin && (
                    <DropdownMenuItem onClick={handleStartTour} data-testid="menu-tour">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Tour Guiado
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Configuración
                  </DropdownMenuItem>
                  {/* Suscripción solo visible para admin de empresa */}
                  {isCompanyAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard/subscription")} data-testid="menu-subscription">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Suscripción
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Guided Tour */}
      {showTour && (
        <GuidedTour 
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
